import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';
import { RefreshCw, Play, ShieldAlert, Wrench, Flame, FileText } from 'lucide-react';
import { AlertSmsCard } from '../../components/custom/AlertSmsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ageBadge = (min) => {
  if (min === null || min === undefined) return { txt: '—', cls: 'text-white/40' };
  const txt = min >= 90 ? `${(min / 60).toFixed(1)}h` : `${min}m`;
  if (min > 120) return { txt, cls: 'text-red-300 font-bold' };
  if (min > 60) return { txt, cls: 'text-amber-300 font-semibold' };
  return { txt, cls: 'text-emerald-300' };
};

export default function AdminQueueWatchdogPage() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [burning, setBurning] = useState(false);
  const [burn, setBurn] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/grading-queue/watchdog`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load watchdog');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/grading-queue/run`);
      const d = r.data;
      toast.success(`Ran: submitted ${d.submit?.submitted ?? 0} · finalized ${d.poll?.finalized ?? 0} · ${d.poll?.stale ?? 0} stale→inline · ${d.poll?.orphaned ?? 0} orphans→inline · oldest now ${d.oldest_waiting_min}m`, { duration: 9000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    }
    setRunning(false);
  };

  const gradeExempt = async () => {
    setBurning(true);
    try {
      const r = await axios.post(`${API}/admin/grading-queue/grade-exempt`);
      setBurn({ start: r.data.backlog || r.data.kicked || 0, done: false });
      toast.success(`Burndown started: ${r.data.kicked} calls dispatched for inline grading across ${r.data.domains} domain(s)`, { duration: 8000 });
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Burndown failed');
    }
    setBurning(false);
  };

  // Live burndown: poll every 4s while a burndown is running so the card
  // counts down as recovered calls finish grading.
  useEffect(() => {
    if (!burn || burn.done) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [burn, load]);

  useEffect(() => {
    if (!burn || burn.done || data === null) return;
    if ((data.exempt_backlog ?? 0) === 0) {
      setBurn((b) => ({ ...b, done: true }));
      toast.success(`Backlog cleared — all ${burn.start} recovered call${burn.start === 1 ? '' : 's'} graded`, { duration: 8000 });
      setTimeout(() => setBurn(null), 12000);
    }
  }, [data, burn]);

  const pending = data?.pending || {};
  const oldest = data?.oldest_min || {};
  const totalPending = Object.values(pending).reduce((a, b) => a + b, 0);
  const worstAge = Math.max(0, ...Object.values(oldest));
  const openBatches = (data?.batches || []).filter((b) => b.status !== 'done' && b.status !== 'failed');
  const recentClosed = (data?.batches || []).filter((b) => b.status === 'done' || b.status === 'failed').slice(0, 12);
  const healthy = totalPending === 0 || worstAge <= 60;
  const heals = data?.heal_events || [];
  const lastHeal = heals[0];
  const healTotal = heals.reduce((a, h) => a + (h.removed || 0), 0);
  const healAgo = lastHeal ? Math.round((Date.now() - new Date(lastHeal.ts).getTime()) / 60000) : null;

  return (
    <AdminLayout title="Queue Watchdog">
      <div className="space-y-4" data-testid="watchdog-page">
        {lastHeal && (
          <div className="rounded-xl border border-teal-500/30 bg-teal-500/10 px-5 py-3 flex items-start gap-2.5" data-testid="watchdog-heal-banner">
            <Wrench className="w-4 h-4 text-teal-300 mt-0.5 shrink-0" />
            <p className="text-sm text-teal-100">
              <span className="font-bold">Self-heal ran:</span> removed {healTotal} duplicate queue doc{healTotal === 1 ? '' : 's'} in
              the last 7 days (latest: {lastHeal.removed} duplicate{lastHeal.removed === 1 ? '' : 's'}, {ageBadge(healAgo).txt} ago).
              Duplicates were the cause of phantom "waiting" calls — a unique index now prevents new ones.
            </p>
          </div>
        )}
        {((data?.exempt_backlog ?? 0) > 0 || burn) && (
          <div className={`rounded-xl border px-5 py-4 ${burn?.done ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-amber-500/30 bg-amber-500/10'}`} data-testid="watchdog-exempt-backlog">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <Flame className={`w-4 h-4 mt-0.5 shrink-0 ${burn?.done ? 'text-emerald-300' : 'text-amber-300'}`} />
                {burn?.done ? (
                  <p className="text-sm text-emerald-100" data-testid="burndown-complete-msg">
                    <span className="font-bold">Backlog cleared</span> — all {burn.start} recovered call{burn.start === 1 ? '' : 's'} graded.
                  </p>
                ) : (
                  <p className="text-sm text-amber-100">
                    <span className="font-bold">{data?.exempt_backlog ?? 0} recovered call{(data?.exempt_backlog ?? 0) === 1 ? '' : 's'}</span> waiting
                    for inline grading (rescued from failed batches).
                    {!burn && ' The stuck-call reaper will pick them up eventually — or grade them all right now.'}
                  </p>
                )}
              </div>
              {!burn && (
                <button onClick={gradeExempt} disabled={burning}
                  className="bg-amber-400 text-slate-950 font-bold text-sm px-4 py-2 rounded-full hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="grade-exempt-btn">
                  <Flame className="w-3.5 h-3.5" /> {burning ? 'Dispatching…' : 'Grade all exempt now'}
                </button>
              )}
            </div>
            {burn && !burn.done && (
              <div className="mt-3" data-testid="burndown-progress">
                {(() => {
                  const remaining = data?.exempt_backlog ?? burn.start;
                  const graded = Math.max(0, burn.start - remaining);
                  const pct = burn.start > 0 ? Math.round((graded / burn.start) * 100) : 0;
                  return (
                    <>
                      <div className="flex justify-between text-xs text-amber-200/80 mb-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse inline-block" />
                          Grading in progress — updates every few seconds
                        </span>
                        <span className="font-bold tabular-nums" data-testid="burndown-count">{graded} / {burn.start} graded ({pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-amber-950/40 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
        <div className={`rounded-xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${healthy ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/40 bg-red-500/10'}`} data-testid="watchdog-summary">
          <div>
            <p className="text-lg font-bold text-white">
              {healthy ? '✓ Queue healthy' : '⚠ Queue backing up'}
              <span className="text-white/60 font-normal text-sm ml-3">
                {totalPending} waiting {totalPending > 0 && `· oldest ${ageBadge(worstAge).txt}`} · threshold 60m
              </span>
            </p>
            <p className="text-xs text-white/50 mt-1">
              queued {pending.queued || 0} · submitting {pending.submitting || 0} · submitted {pending.submitted || 0}
              {data && !data.anthropic_key_present && <span className="text-red-300 font-bold ml-2">· ⚠ NO ANTHROPIC KEY</span>}
              {data?.orphans > 0 && <span className="text-amber-300 font-bold ml-2">· {data.orphans} orphaned items (parent batch closed)</span>}
              {data?.missing_created_at > 0 && <span className="text-amber-300 ml-2">· {data.missing_created_at} missing timestamps</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="text-white/60 hover:text-white p-2" data-testid="watchdog-refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={runNow} disabled={running}
              className="bg-teal-400 text-slate-950 font-bold text-sm px-4 py-2 rounded-full hover:bg-teal-300 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="watchdog-run-btn">
              <Play className="w-3.5 h-3.5" /> {running ? 'Running…' : 'Run submit + poll + rescue'}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="watchdog-open-batches">
          <p className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">Open batches ({openBatches.length})</p>
          {openBatches.length === 0 ? (
            <p className="text-sm text-white/40">No batches in flight.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-white/40">
                  <th className="pb-2">Batch</th><th className="pb-2">Status</th><th className="pb-2">Age</th>
                  <th className="pb-2">Last update</th><th className="pb-2">Items pending</th><th className="pb-2">Anthropic counts</th>
                </tr>
              </thead>
              <tbody>
                {openBatches.map((b) => {
                  const age = ageBadge(b.age_min);
                  const upd = ageBadge(b.updated_min);
                  const rc = b.request_counts || {};
                  return (
                    <tr key={b.batch_id} className="border-t border-white/5" data-testid={`watchdog-batch-${b.batch_id}`}>
                      <td className="py-2 font-mono text-xs text-white/70">{(b.batch_id || '').slice(0, 22)}…</td>
                      <td className="py-2 text-white/80">{b.status}</td>
                      <td className={`py-2 ${age.cls}`}>{age.txt}</td>
                      <td className={`py-2 ${upd.cls}`}>{upd.txt}</td>
                      <td className="py-2 text-white/80">{b.pending_items}</td>
                      <td className="py-2 text-white/50 text-xs">ok {rc.succeeded ?? 0} · proc {rc.processing ?? 0} · err {rc.errored ?? 0} · exp {rc.expired ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="watchdog-recent-batches">
          <p className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">Recently closed (7 days)</p>
          {recentClosed.length === 0 ? <p className="text-sm text-white/40">None in the last 7 days.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {recentClosed.map((b) => (
                <div key={b.batch_id} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs" data-testid={`watchdog-closed-${b.batch_id}`}>
                  <span className="font-mono text-white/60">{(b.batch_id || '').slice(0, 18)}…</span>
                  <span className={`ml-2 font-semibold ${b.status === 'done' ? 'text-emerald-300' : 'text-red-300'}`}>{b.status}</span>
                  <span className="text-white/40 ml-2">{ageBadge(b.age_min).txt} ago</span>
                  {b.pending_items > 0 && (
                    <span className="text-amber-300 font-bold ml-2 inline-flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> {b.pending_items} stuck
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-white/35 mt-3">
            Items stuck under a closed batch are rescued to inline grading automatically every poll (and by the Run button). Anything older than {data?.stale_threshold_hours || 26}h falls back to inline regardless.
          </p>
        </div>

        <AlertSmsCard />
        <a href="/admin/weekly-reports" className="inline-flex items-center gap-1.5 text-teal-300 hover:text-teal-200 text-sm font-semibold" data-testid="past-reports-link">
          <FileText className="w-4 h-4" /> Past weekly ops reports →
        </a>
      </div>
    </AdminLayout>
  );
}
